import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import {PartJSON} from 'biojs/Part';
import {SchemaTag, Tag} from 'folder-items/FileConstants';
import {SchemaValidationErrorRecord} from 'schemas/SchemaValidationConstants';
import * as TagSchemaUtil from 'tag-schema/util/TagSchemaUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ElementKeyboardEvent, Optional} from 'util/Types';

import SchemaFieldValueError from './SchemaFieldValueError';
import TagValueEditor from './TagValueEditor';
import TagValueViewer from './TagValueViewer';

export interface TagValueCellProps {
  errors?: Array<SchemaValidationErrorRecord> | null;
  fileId?: string;
  itemLinkAllowed: boolean;
  isEditing: boolean;
  onKeyDown: (e: ElementKeyboardEvent) => void;
  onUpdateTag: (tagIndex: number, newValue: Tag) => void;
  sequenceParts: Optional<Array<PartJSON>>;
  schemaFieldSelectorId?: string;
  shouldAutoFocus: boolean;
  tagIndex: number;
  tag: SchemaTag;
}

/**
 * Component for viewing and editing a field value as part of the metadata editor, including errors
 * and a "trash" button to remove the field in some cases.
 */
class TagValueCell extends React.Component<TagValueCellProps> {
  private getDefaultTagValue = (): SchemaTag => ({
    ...this.props.tag,
    name: this.props.tag.name || '',
    value: this.props.tag.value || '',
    url: this.props.tag.url || '',
  });

  private handleChangeValue = (newValue: Tag): void => {
    this.props.onUpdateTag(this.props.tagIndex, newValue);
  };

  render(): JSX.Element {
    const isItemLinkField =
      this.props.tag.tagSchemaField &&
      TagSchemaUtil.isItemLinkFieldType(this.props.tag.tagSchemaField.fieldType);
    return (
      <div
        className={classNames('u-width-full', {
          'u-cursor-text': !this.props.isEditing && !isItemLinkField,
        })}
        {...getTestProps({component: 'TagValueCell', element: 'root', key: this.props.tag.name})}
      >
        {this.props.isEditing ? (
          <div className="metadata-tagValueInput u-width-full">
            <TagValueEditor
              autoFocus={this.props.shouldAutoFocus}
              fileId={this.props.fileId}
              itemLinkAllowed={this.props.itemLinkAllowed}
              onChange={this.handleChangeValue}
              onKeyDown={this.props.onKeyDown}
              schemaFieldSelectorId={this.props.schemaFieldSelectorId}
              sequenceParts={this.props.sequenceParts}
              tag={this.getDefaultTagValue()}
            />
          </div>
        ) : (
          <TagValueViewer tag={this.props.tag} />
        )}
        {/* Filter out duplicate errors (e.g. a field linking to 2 files outside the registry),
           since showing the same error to the user is confusing. */}
        {this.props.errors &&
          // eslint-disable-next-line no-restricted-syntax
          _.uniq(this.props.errors, 'errorLabel').map((error, i) => (
            <SchemaFieldValueError
              errorLabel={error.errorLabel}
              schemaFieldRenderData={error.field!}
              message={error.message}
              key={error.errorLabel}
            />
          ))}
      </div>
    );
  }
}

export default TagValueCell;
